/**
 * Home hero collage — pointer parallax.
 *
 * Each layer declares a data-depth; positive values drift with the cursor,
 * negative ones against it, which is what gives the collage its sense of
 * depth. Vertical travel is damped to 60% of horizontal so the pieces slide
 * rather than bounce.
 */

// Easing applied to the gap between the cursor target and the current offset.
const FOLLOW = 0.035;

// The collage is still animating itself in on load, so the parallax fades up
// over this window instead of snapping to full strength on the first move.
const RAMP_MS = 1800;

// Below this the offset has settled and the loop can stop.
const SETTLED = 0.0005;

const initStage = (stage) => {
  const layers = Array.from(stage.querySelectorAll('[data-depth]'));

  if (!layers.length) return;

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  let targetX = 0;
  let targetY = 0;
  let currentX = 0;
  let currentY = 0;
  let firstMove = 0;
  let frame = null;

  const step = () => {
    const elapsed = firstMove
      ? Math.min(1, (performance.now() - firstMove) / RAMP_MS)
      : 0;
    const ramp = elapsed * elapsed * (3 - 2 * elapsed);

    currentX += (targetX * ramp - currentX) * FOLLOW;
    currentY += (targetY * ramp - currentY) * FOLLOW;

    layers.forEach((layer) => {
      const depth = parseFloat(layer.dataset.depth) || 0;
      const x = (currentX * depth).toFixed(2);
      const y = (currentY * depth * 0.6).toFixed(2);

      layer.style.transform = `translate3d(${x}px, ${y}px, 0)`;
    });

    const restless =
      Math.abs(targetX * ramp - currentX) > SETTLED ||
      Math.abs(targetY * ramp - currentY) > SETTLED ||
      elapsed < 1;

    frame = restless ? requestAnimationFrame(step) : null;
  };

  const kick = () => {
    if (!frame) frame = requestAnimationFrame(step);
  };

  window.addEventListener('mousemove', (event) => {
    const box = stage.getBoundingClientRect();

    targetX = (event.clientX - (box.left + box.width / 2)) / (box.width / 2);
    targetY = (event.clientY - (box.top + box.height / 2)) / (box.height / 2);

    if (!firstMove) firstMove = performance.now();

    kick();
  });

  window.addEventListener('mouseleave', () => {
    targetX = 0;
    targetY = 0;
    kick();
  });
};

const initAll = () => {
  document.querySelectorAll('[data-home-hero-stage]').forEach((stage) => {
    if (stage.dataset.homeHeroStageInitialized) {
      return;
    }

    stage.dataset.homeHeroStageInitialized = 'true';
    initStage(stage);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAll);
} else {
  initAll();
}
